import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getGetStorageUsageApiV1UsageStorageGetQueryKey } from '../../../generated/api/usage/usage';
import { fileApi } from '../services/fileApi';
import { FileUploadParams, FileUpdateParams, ListFilesParams, MoveFileParams, CreateTextFileParams, FileAccessMode, FileMetadata } from '../types';

export const FILES_QUERY_KEY = 'files';
const FOLDER_KEYS = {
  all: ['folders'] as const,
  contents: (id: string | null) => [...FOLDER_KEYS.all, 'contents', id] as const,
};

export function useFileUrl(fileId: string | null | undefined) {
  return useQuery({
    queryKey: [FILES_QUERY_KEY, 'url', fileId],
    queryFn: () => {
      if (!fileId) return null;
      return fileApi.getPublicUrl(fileId);
    },
    enabled: !!fileId,
    staleTime: 1000 * 60 * 5, // URL stays fresh for 5 minutes
  });
}

/**
 * Server-rendered preview of one file (Excel / Word / PowerPoint / delimited text).
 *
 * Held for a long `staleTime` because the answer is a pure function of the file's
 * bytes, and re-parsing a workbook to redraw a dialog the user closed and reopened
 * is the most expensive read in this module (`_FILE_PREVIEW_MAX_CONCURRENT` caps
 * it per tenant, so a re-fetch storm is answered with 429s rather than latency).
 *
 * No retry: every refusal this route gives — unsupported format, too large,
 * unreadable file — is deterministic, so a retry re-parses the same bytes to reach
 * the same answer and only delays the message the user needs to read.
 */
export function useFilePreview(fileId: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [FILES_QUERY_KEY, 'preview', fileId],
    queryFn: () => fileApi.fetchPreview(fileId as string),
    enabled: enabled && !!fileId,
    retry: false,
    staleTime: 1000 * 60 * 10,
  });
}

export function useFiles(params?: ListFilesParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [FILES_QUERY_KEY, params],
    queryFn: () => fileApi.list(params),
    enabled: options?.enabled ?? true,
  });
}

// The batch endpoint caps each request at _ATTACHMENTS_BATCH_MAX_REFS (200,
// modules/file_management/api/routes.py). Chunk to that ceiling so an unbounded
// caller (a category with hundreds of products, a large bundle list) never trips
// the 400 — it just fans out into ⌈N/200⌉ requests, still far fewer than the N
// per-row requests this replaces.
const ATTACHMENTS_BATCH_CHUNK_SIZE = 200;

/**
 * Batch-fetch attachments for many entity refs, returning a map keyed by
 * `attached_to` ref. This is the N+1 fix for list tables that render an
 * attachments column per row: instead of each row firing its own
 * `useFiles({ attached_to })` (which blew past the file-listing endpoint's
 * per-tenant in-flight cap and produced a "Rate limit exceeded" toast storm), the
 * table fetches all visible rows' attachments here and passes each row's list to
 * `AttachmentsPreview` via its `files` prop.
 *
 * Refs are de-duplicated and sorted so the query key is stable regardless of row
 * order, then chunked to the endpoint's per-request cap. Refs the caller can't
 * access are absent from the map (treated as empty).
 */
export function useFilesBatch(refs: string[]) {
  const sortedRefs = [...new Set(refs)].sort();
  return useQuery({
    queryKey: [FILES_QUERY_KEY, 'batch', sortedRefs],
    queryFn: async () => {
      const chunks: string[][] = [];
      for (let i = 0; i < sortedRefs.length; i += ATTACHMENTS_BATCH_CHUNK_SIZE) {
        chunks.push(sortedRefs.slice(i, i + ATTACHMENTS_BATCH_CHUNK_SIZE));
      }
      const results = await Promise.all(
        chunks.map((chunk) => fileApi.listAttachmentsBatch(chunk))
      );
      const merged: Record<string, FileMetadata[]> = {};
      for (const result of results) Object.assign(merged, result.data);
      return { data: merged };
    },
    enabled: sortedRefs.length > 0,
  });
}

export function useFile(id: string) {
  return useQuery({
    queryKey: [FILES_QUERY_KEY, id],
    queryFn: () => fileApi.get(id),
    enabled: !!id,
  });
}

/**
 * Fetch a file's malware scan status, lazily.
 *
 * Only fetches for uploaded files stored in S3 — local-dev files and external
 * (linked) files have no GuardDuty scanning and always resolve to
 * `not_applicable`, so we skip the network call entirely for them. This keeps
 * the warning a no-op locally and avoids per-row requests except where a real
 * status exists (S3 / production).
 */
export function useMalwareScanStatus(file: Pick<FileMetadata, 'id' | 'file_type' | 'storage_backend'>) {
  const enabled = file.file_type === 'uploaded' && file.storage_backend === 's3';
  return useQuery({
    queryKey: [FILES_QUERY_KEY, 'scan-status', file.id],
    queryFn: () => fileApi.getScanStatus(file.id),
    enabled,
    staleTime: 1000 * 30, // status can transition from not_scanned shortly after upload
    select: (res) => res.data.malware_scan_status,
  });
}

/**
 * Invalidate the tenant storage meter.
 *
 * The counter moves on the server for every create/delete of an uploaded file,
 * but the meter renders right beside the upload control — a stale figure there
 * is worse than none, because a user reads it as the number the next upload will
 * be checked against. Any mutation that adds or removes bytes must call this.
 */
function invalidateStorageUsage(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({
    queryKey: getGetStorageUsageApiV1UsageStorageGetQueryKey(),
  });
}

export function useUploadFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: FileUploadParams) => fileApi.upload(params),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [FILES_QUERY_KEY] });
      invalidateStorageUsage(queryClient);
      // Invalidate folder contents if uploading to a folder
      if (variables.folder_id) {
        queryClient.invalidateQueries({ queryKey: FOLDER_KEYS.contents(variables.folder_id) });
      } else {
        queryClient.invalidateQueries({ queryKey: FOLDER_KEYS.contents(null) });
      }
    },
  });
}

export function useUpdateFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: FileUpdateParams }) =>
      fileApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FILES_QUERY_KEY] });
    },
  });
}

export function useDeleteFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => fileApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FILES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: FOLDER_KEYS.all });
      invalidateStorageUsage(queryClient);
    },
  });
}

export function useMoveFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: MoveFileParams }) =>
      fileApi.move(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FILES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: FOLDER_KEYS.all });
    },
  });
}

export function useCreateTextFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: CreateTextFileParams) => fileApi.createTextFile(params),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [FILES_QUERY_KEY] });
      invalidateStorageUsage(queryClient);
      if (variables.folder_id) {
        queryClient.invalidateQueries({ queryKey: FOLDER_KEYS.contents(variables.folder_id) });
      } else {
        queryClient.invalidateQueries({ queryKey: FOLDER_KEYS.contents(null) });
      }
    },
  });
}

/**
 * Hook that resolves a loadable `<img src>` URL for an authenticated file.
 *
 * Three paths, all CORS-free so an `<img>` loads on every hostname/CloudFront
 * distribution (the failure that broke attachment thumbnails in prod was a
 * cross-origin `fetch` to S3 that depended on bucket CORS + a CSP `connect-src`
 * bucket entry — a fragile infra chain that had drifted):
 *
 * 1. **Public** — point straight at the anonymous, long-cached `/public` route.
 * 2. **S3-backed link/private** — ask `/download-url` for a short-lived presigned
 *    URL and use it *directly* as the src. Governed by CSP `img-src https:`; the
 *    bytes flow S3→client (egress optimization preserved) with no cross-origin
 *    `fetch`, so no bucket CORS is involved.
 * 3. **Local backend** (`/download-url` returns null) — fall back to a same-origin
 *    authed blob fetch (`/link`/`/download` now stream), which is covered by CSP
 *    `connect-src 'self'`.
 */
export function useAuthenticatedImageUrl(
  fileId: string | null | undefined,
  isPublic: boolean,
  accessMode?: FileAccessMode
) {
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Only genuinely public assets are servable without auth — those use the
  // unauthenticated /public route directly so an <img> tag can load them.
  const isPubliclyServable = isPublic || accessMode === 'public';

  useEffect(() => {
    if (!fileId) {
      setResolvedUrl(null);
      return;
    }

    // Publicly-servable files: point the <img> straight at the /public URL (no
    // fetch, no auth header — the route is anonymous and long-cached).
    if (isPubliclyServable) {
      setResolvedUrl(fileApi.getPublicUrl(fileId));
      return;
    }

    // Link/private files. Track resources created inside this effect in locals so
    // cleanup tears down THIS effect's work (reading render-scoped state in cleanup
    // would capture the pre-fetch value). We only ever create an object URL on the
    // local blob-fallback path; a presigned https URL needs no revoke.
    let mounted = true;
    let objectUrl: string | null = null;
    setIsLoading(true);
    setError(null);

    fileApi.fetchDownloadUrl(fileId, 'inline')
      .then(async (directUrl) => {
        if (!mounted) return;
        if (directUrl) {
          // S3: use the presigned URL directly as the <img src> — no blob, no CORS.
          setResolvedUrl(directUrl);
          setIsLoading(false);
          return;
        }
        // Local backend: stream the bytes same-origin and wrap in an object URL.
        const blob = await fileApi.fetchAsBlob(fileId, accessMode);
        if (mounted) {
          objectUrl = URL.createObjectURL(blob);
          setResolvedUrl(objectUrl);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        if (mounted) {
          setError(err);
          setIsLoading(false);
        }
      });

    return () => {
      mounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [fileId, isPubliclyServable, accessMode]);

  return { url: resolvedUrl, isLoading, error };
}
